import { replace } from 'redux-first-history';
import _ from 'underscore';
import { getLogger } from '../lib/devMode';
import { sanitizeQuery } from "../utils/sanitizeQuery";

/**
 * Workaround the query update:
 * since different hooks can trigger update to the same URL - preserve the latest pending update
 * (dispatch) before triggering the next one.
 *
 * @type {string}
 */
let updatePendingQuery = '';

/**
 * Handler for a debounced function for URL query update.
 *
 * @type {function}
 */
let updatePendingHandle;

/**
 * Counter for number of URL parameters (query) updates (logging purposes).
 *
 * @type {number}
 */
let updateQueryCounter = 1;

export const getLocation = (state) => state.router.location;

const apply = (location, dispatch, paramsNew) => {
    const logger = getLogger();
    const searchNew = '?' + paramsNew.toString();
    if (document.location.search !== searchNew && updatePendingQuery !== searchNew) {
        updatePendingQuery = searchNew;
        logger.debug(`[QUERY UPDATE] START @ ${++updateQueryCounter}`);
        logger.debug({
            current: location.search,
            new____: '?' + paramsNew.toString(),
            pending: updatePendingQuery
        });
        logger.debug('[QUERY UPDATE] END');

        dispatch(
            replace({
                pathname: location.pathname,
                search: '?' + paramsNew.toString(),
            })
        );
    }
}

/**
 * Unified function to trigger URL query parameters update.
 * It's responsible to resolve and/or substitute all fields resulting in all parameters being set.
 *
 * No direct changes to the query parameters should be performed without usage of
 * the current function.
 *
 * @param dispatch
 * @param getState
 * @param [params]
 */
export const updateQueryParameters = (dispatch, getState, params = {}) => {
    const logger = getLogger();
    const location = getLocation(getState());

    // We use updateQueryParameters only on the "/" page.
    // updateQueryParameters is called via the reducer, so we don't need to change
    // the query string on other pages.
    if (location.pathname !== "/") return;

    let paramsRawNew = {};
    const currentQuery = Array.from(new URLSearchParams(document.location.search))
        .reduce((memo, item) => {
            memo[item[0]] = item[1];
            return memo;
        }, {})
    Object.keys({...currentQuery, ...params}).map(key=> {
        if (key in params && key === 'domains') {
            const domains = params[key] ?? currentQuery[key] ?? '';
            paramsRawNew[key] = typeof domains === 'string' ? domains?.split(',') : domains;
        } else {
            paramsRawNew[key] = params[key] ?? currentQuery[key];
        }
    });

    // Explicitly remove it from the query. It should be applied only once on initial app launch.
    delete paramsRawNew['user_token'];
    delete paramsRawNew['payment_page_token'];

    let paramsNew = new URLSearchParams(paramsRawNew);
    paramsNew.sort();

    sanitizeQuery(paramsNew);

    logger.debug({
        action: 'QUERY UPDATE REQUEST',
        pending: !!typeof updatePendingHandle,
        params: paramsRawNew
    });
    if (typeof updatePendingHandle === 'function') {
        updatePendingHandle?.cancel();
    }
    updatePendingHandle = _.debounce(apply.bind(null, location, dispatch, paramsNew), 1);
    updatePendingHandle();
};
